import React from 'react';
import { Search, Filter, Calendar, ChevronDown } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { handleSanitizedChange } from '../../utils/inputSanitization';

const TournamentFilters = ({ 
  searchTerm, 
  setSearchTerm, 
  filterCategory, 
  setFilterCategory,
  filterYear,
  setFilterYear,
  showUpcomingOnly,
  setShowUpcomingOnly
}) => {
  const { isDarkMode } = useTheme();
  const [openDropdown, setOpenDropdown] = React.useState(null);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'mens_open', label: "Men's Open" },
    { value: 'mens_mid_am', label: "Men's Mid-Am" },
    { value: 'mens_senior', label: "Men's Senior" },
    { value: 'mens_super_senior', label: "Men's Super Senior" },
    { value: 'ladies_open', label: "Ladies Open" },
    { value: 'ladies_mid_am', label: "Ladies Mid-Am" },
    { value: 'ladies_senior', label: "Ladies Senior" },
    { value: 'ladies_super_senior', label: "Ladies Super Senior" },
    { value: 'junior_boys', label: 'Junior Boys' },
    { value: 'junior_girls', label: 'Junior Girls' },
    { value: 'mixed', label: 'Mixed' },
    { value: 'country_districts', label: 'Country Districts' }
  ];

  const currentYear = new Date().getFullYear();
  const years = [
    { value: 'all', label: 'All Years' },
    ...Array.from({ length: 5 }, (_, i) => {
      const year = currentYear - i;
      return { value: year.toString(), label: year.toString() };
    })
  ];

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const handleDropdownClick = (e, dropdown) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  return (
    <div className={`p-4 sm:p-6 rounded-xl border ${
      isDarkMode
        ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
        : 'bg-fsgu-white border-fsgu-silver-200'
    }`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Search */}
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search className={`absolute left-3 top-3 w-5 h-5 pointer-events-none ${
            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-500'
          }`} />
          <input
            type="text"
            placeholder="Search tournaments..."
            value={searchTerm}
            onChange={(e) => handleSanitizedChange(e, setSearchTerm, 'text')}
            className={`w-full pl-10 pr-4 py-2.5 rounded-lg border transition-colors ${
              isDarkMode
                ? 'bg-fsgu-gray-900 border-fsgu-gray-600 text-fsgu-silver-100 placeholder-fsgu-silver-500 focus:border-fsgu-green-500'
                : 'bg-fsgu-white border-fsgu-silver-300 text-fsgu-silver-900 placeholder-fsgu-silver-400 focus:border-fsgu-green-500'
            } focus:outline-none focus:ring-2 focus:ring-fsgu-green-500/20`}
          />
        </div>

        {/* Category Filter */}
        <div className="relative">
          <button
            onClick={(e) => handleDropdownClick(e, 'category')}
            className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-colors ${
              openDropdown === 'category'
                ? isDarkMode
                  ? 'border-fsgu-green-500 bg-fsgu-green-500/10'
                  : 'border-fsgu-green-500 bg-fsgu-green-500/10'
                : isDarkMode
                ? 'bg-fsgu-gray-900 border-fsgu-gray-600 hover:border-fsgu-green-500/50'
                : 'bg-fsgu-white border-fsgu-silver-300 hover:border-fsgu-green-500/50'
            } focus:outline-none focus:ring-2 focus:ring-fsgu-green-500/20`}
          >
            <span className={isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'}>
              {categories.find(c => c.value === filterCategory)?.label || 'All Categories'}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${
              openDropdown === 'category' ? 'rotate-180' : ''
            } ${isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-500'}`} />
          </button>

          {/* Dropdown Menu */}
          {openDropdown === 'category' && (
            <div className={`absolute top-full left-0 right-0 mt-2 rounded-lg shadow-lg border overflow-hidden z-50 max-h-80 overflow-y-auto ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200'
            }`}>
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => {
                    setFilterCategory(cat.value);
                    setOpenDropdown(null);
                  }}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                    filterCategory === cat.value
                      ? isDarkMode
                        ? 'text-fsgu-green-400 bg-fsgu-green-500/10'
                        : 'text-fsgu-green-500 bg-fsgu-green-500/10'
                      : isDarkMode
                      ? 'text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-700'
                      : 'text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-50'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Year Filter */}
        <div className="relative">
          <button
            onClick={(e) => handleDropdownClick(e, 'year')}
            className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-colors ${
              openDropdown === 'year'
                ? isDarkMode
                  ? 'border-fsgu-green-500 bg-fsgu-green-500/10'
                  : 'border-fsgu-green-500 bg-fsgu-green-500/10'
                : isDarkMode
                ? 'bg-fsgu-gray-900 border-fsgu-gray-600 hover:border-fsgu-green-500/50'
                : 'bg-fsgu-white border-fsgu-silver-300 hover:border-fsgu-green-500/50'
            } focus:outline-none focus:ring-2 focus:ring-fsgu-green-500/20`}
          >
            <span className={isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'}>
              {years.find(y => y.value === filterYear)?.label || 'All Years'}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${
              openDropdown === 'year' ? 'rotate-180' : ''
            } ${isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-500'}`} />
          </button>

          {/* Dropdown Menu */}
          {openDropdown === 'year' && (
            <div className={`absolute top-full left-0 right-0 mt-2 rounded-lg shadow-lg border overflow-hidden z-50 ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200'
            }`}>
              {years.map((year) => (
                <button
                  key={year.value}
                  onClick={() => {
                    setFilterYear(year.value);
                    setOpenDropdown(null);
                  }}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                    filterYear === year.value
                      ? isDarkMode
                        ? 'text-fsgu-green-400 bg-fsgu-green-500/10'
                        : 'text-fsgu-green-500 bg-fsgu-green-500/10'
                      : isDarkMode
                      ? 'text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-700'
                      : 'text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-50'
                  }`}
                >
                  {year.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TournamentFilters;